import { Share2, MessageCircle, Link2, Instagram } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export type ShareEventData = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  cafe_name?: string | null;
  cafe_location?: string | null;
  distance_km?: number | null;
  club_name?: string | null;
};

const SHARE_BASE = "https://brulo.fit/evento";

function formatDate(date: string) {
  return new Date(`${date}T00:00:00`).toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function buildShareUrl(id: string) {
  return `${SHARE_BASE}/${id}`;
}

function buildWhatsAppMessage(ev: ShareEventData) {
  const date = formatDate(ev.event_date);
  const time = ev.event_time?.slice(0, 5) ?? "";
  const place = ev.cafe_name ?? ev.meeting_point;
  const addr = ev.cafe_location ? ` — ${ev.cafe_location}` : "";
  const dist = ev.distance_km != null ? `\n🚴 ${ev.distance_km} km` : "";
  return `🏃 ${ev.name}\n📅 ${date} a las ${time}\n📍 ${place}${addr}${dist}\n\nÚnete en ${buildShareUrl(ev.id)}`;
}

async function copyLink(ev: ShareEventData) {
  const url = buildShareUrl(ev.id);
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  } catch {
    toast.error("No se pudo copiar el link");
  }
}

function shareWhatsApp(ev: ShareEventData) {
  const text = encodeURIComponent(buildWhatsAppMessage(ev));
  window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
}

async function downloadStoryImage(ev: ShareEventData) {
  const size = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    toast.error("Tu navegador no soporta esta función");
    return;
  }

  const BG = "#1a1714";
  const CREAM = "#f5f0eb";
  const MUTED = "#a99e93";
  const CORAL = "#D85A30";

  // Background
  const grad = ctx.createLinearGradient(0, 0, 0, size);
  grad.addColorStop(0, "#2a1a0e");
  grad.addColorStop(0.5, "#1a1310");
  grad.addColorStop(1, BG);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Coral accent bar
  ctx.fillStyle = CORAL;
  ctx.fillRect(80, 80, 120, 6);

  // Wordmark
  ctx.fillStyle = CREAM;
  ctx.font = "600 36px Inter, system-ui, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText("brulo.fit", 80, 110);

  // Eyebrow (club)
  let cursorY = 240;
  if (ev.club_name) {
    ctx.fillStyle = CORAL;
    ctx.font = "700 28px Inter, system-ui, sans-serif";
    ctx.letterSpacing = "8px";
    ctx.fillText(ev.club_name.toUpperCase(), 80, cursorY);
    cursorY += 60;
  }

  // Event name (wrap)
  ctx.fillStyle = CREAM;
  ctx.font = "700 88px Georgia, 'Times New Roman', serif";
  const maxWidth = size - 160;
  const words = ev.name.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  for (const l of lines.slice(0, 3)) {
    ctx.fillText(l, 80, cursorY);
    cursorY += 100;
  }

  cursorY += 40;
  // Divider
  ctx.fillStyle = "#3a2e26";
  ctx.fillRect(80, cursorY, size - 160, 2);
  cursorY += 50;

  // Date
  ctx.fillStyle = MUTED;
  ctx.font = "500 26px Inter, system-ui, sans-serif";
  ctx.fillText("FECHA", 80, cursorY);
  ctx.fillStyle = CREAM;
  ctx.font = "600 38px Inter, system-ui, sans-serif";
  const dateStr = formatDate(ev.event_date);
  ctx.fillText(`${dateStr} · ${ev.event_time?.slice(0, 5)}`, 80, cursorY + 36);
  cursorY += 120;

  // Place
  ctx.fillStyle = MUTED;
  ctx.font = "500 26px Inter, system-ui, sans-serif";
  ctx.fillText("PUNTO DE REUNIÓN", 80, cursorY);
  ctx.fillStyle = CREAM;
  ctx.font = "600 38px Inter, system-ui, sans-serif";
  ctx.fillText(ev.cafe_name ?? ev.meeting_point, 80, cursorY + 36);
  if (ev.cafe_location) {
    ctx.fillStyle = MUTED;
    ctx.font = "400 28px Inter, system-ui, sans-serif";
    ctx.fillText(ev.cafe_location, 80, cursorY + 84);
  }

  // Distance pill (top right)
  if (ev.distance_km != null) {
    ctx.fillStyle = CORAL;
    ctx.font = "700 96px Georgia, 'Times New Roman', serif";
    ctx.textAlign = "right";
    ctx.fillText(`${ev.distance_km}`, size - 80, 220);
    ctx.fillStyle = CREAM;
    ctx.font = "600 32px Inter, system-ui, sans-serif";
    ctx.fillText("km", size - 80, 320);
    ctx.textAlign = "left";
  }

  // CTA bottom
  const ctaY = size - 180;
  ctx.fillStyle = CORAL;
  ctx.fillRect(80, ctaY, size - 160, 100);
  ctx.fillStyle = "#1a1714";
  ctx.font = "700 36px Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ÚNETE EN BRULO.FIT", size / 2, ctaY + 32);
  ctx.textAlign = "left";

  // Download
  canvas.toBlob((blob) => {
    if (!blob) {
      toast.error("No se pudo generar la imagen");
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brulo-${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Imagen descargada — súbela a tus stories");
  }, "image/png");
}

export function ShareEventButton({
  event,
  variant = "icon",
}: {
  event: ShareEventData;
  variant?: "icon" | "full";
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={
            variant === "full"
              ? "inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-background/60 text-sm font-semibold hover:border-primary/50 hover:text-primary transition"
              : "inline-flex items-center justify-center w-9 h-9 rounded-md border border-border bg-background/60 text-muted-foreground hover:border-primary/50 hover:text-primary transition"
          }
          aria-label="Compartir evento"
        >
          <Share2 className="w-4 h-4" />
          {variant === "full" && <span>Compartir</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => shareWhatsApp(event)} className="gap-2 cursor-pointer">
          <MessageCircle className="w-4 h-4 text-primary" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => copyLink(event)} className="gap-2 cursor-pointer">
          <Link2 className="w-4 h-4 text-primary" />
          Copiar link
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => downloadStoryImage(event)} className="gap-2 cursor-pointer">
          <Instagram className="w-4 h-4 text-primary" />
          Instagram Stories
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
